import {inject, Injectable} from "@angular/core";
import {MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition} from "@angular/material/snack-bar";

export interface SnackbarConfig {
  horizontalPosition?: MatSnackBarHorizontalPosition;
  verticalPosition?: MatSnackBarVerticalPosition;
  durationInSeconds?: number;
}


@Injectable({
  providedIn: 'root'
})
export class SnackbarService {
  /**
   * A collection of notification messages used within the application.
   * Contains user-facing messages for various scenarios.
   * The keys represent the identifier for the specific message,
   * while the values are the corresponding message strings displayed to the user.
   *
   * Properties:
   * - unexpectedErrorOccurred: Message shown when an unexpected error occurs.
   */
  public readonly NOTIFICATION_MESSAGES = {
    unexpectedErrorOccurred: 'Unexpected error occurred. Please try again later.',
    cantProcessRequestAtThisTime: 'System is already busy processing a request. Please try again later.'
  };

  private _snackbar: MatSnackBar = inject(MatSnackBar);

  private readonly SNACKBAR_DEFAULT_CONFIG: SnackbarConfig = {
    horizontalPosition: 'right',
    verticalPosition: 'top',
    durationInSeconds: 5 * 1000
  }

  /**
   * Opens a snackbar notification with the given message and configuration options.
   *
   * @param {string} message - The message to display in the snackbar.
   * @param {MatSnackBarHorizontalPosition} [horizontalPosition='right'] - The horizontal position of the snackbar. Defaults to 'right'.
   * @param {MatSnackBarVerticalPosition} [verticalPosition='top'] - The vertical position of the snackbar. Defaults to 'top'.
   * @param {number} [durationInSeconds=5000] - The duration in milliseconds for which the snackbar will be visible. Defaults to 5000.
   * @return {void} This method does not return a value.
   */
  public openSnackBar(message: string,
                      horizontalPosition: MatSnackBarHorizontalPosition = 'right',
                      verticalPosition: MatSnackBarVerticalPosition = 'top',
                      durationInSeconds = 5 * 1000) {
    if (message?.length > 0) {
      this._snackbar.open(message, 'Dismiss', {
        horizontalPosition: horizontalPosition,
        verticalPosition: verticalPosition,
        duration: durationInSeconds,
      });
    }
  }

  /**
   * Displays a snackbar notification for an unexpected error occurrence.
   *  Displays the message: Unexpected error occurred.
   *  See {@link NOTIFICATION_MESSAGES.unexpectedErrorOccurred}
   * @param {SnackbarConfig} [config={}] - Optional configuration object to customize the snackbar behavior.
   * @return {void} - Does not return a value.
   */
  public openUnexpectedErrorOccurred(config: SnackbarConfig = {}) {
    this._snackbar.open(
      this.NOTIFICATION_MESSAGES.unexpectedErrorOccurred,
      'Dismiss',
      this.mergeConfig(config)
    );
  }

  /**
   * Displays a snackbar notification indicating that the request cannot be processed at this time.
   *  Displays the message: Cannot process request at this time.
   *  See {@link NOTIFICATION_MESSAGES.cantProcessRequestAtThisTime}
   * @param config - Optional configuration object to customize the snackbar behavior.
   * @return {void} - Does not return a value.
   */
  public openCantProcessRequestAtThisTime(config: SnackbarConfig = {}) {
    this._snackbar.open(
      this.NOTIFICATION_MESSAGES.cantProcessRequestAtThisTime,
      'Dismiss',
      this.mergeConfig(config)
    );
  }

  private mergeConfig(config: SnackbarConfig): SnackbarConfig {
    return config ? {...this.SNACKBAR_DEFAULT_CONFIG, ...config} : this.SNACKBAR_DEFAULT_CONFIG;
  }
}
